import { useCallback, useEffect, useRef, useState } from 'react';
import { User } from 'firebase/auth';
import { Statsig } from 'statsig-react';
import { STATSIG_FLAGS } from 'consts';
import { EmployeeStore } from 'types/employeeStore';

type StoreGateState = {
  cache: Map<string, boolean>;
  inflight: Map<string, Promise<boolean>>;
  lastSelectedStoreKey: string | null;
};

const storeGateStateByFlag = new Map<STATSIG_FLAGS, StoreGateState>();

const getStoreStatsigKey = (store: EmployeeStore) => `${store.id}:${store.country.country_code}`;

const getStoreGateState = (flag: STATSIG_FLAGS): StoreGateState => {
  let state = storeGateStateByFlag.get(flag);
  if (!state) {
    state = {
      cache: new Map(),
      inflight: new Map(),
      lastSelectedStoreKey: null,
    };
    storeGateStateByFlag.set(flag, state);
  }
  return state;
};

const buildStatsigUser = (store: EmployeeStore, currentUser: User) => ({
  userID: currentUser.uid,
  email: currentUser.email || undefined,
  country: store.country.country_code,
  custom: {
    store_id: String(store.id),
    country_code: store.country.country_code,
  },
});

const checkStoreGateForStore = async (
  flag: STATSIG_FLAGS,
  store: EmployeeStore,
  currentUser: User,
): Promise<boolean> => {
  const storeKey = getStoreStatsigKey(store);
  const gateState = getStoreGateState(flag);

  let inflight = gateState.inflight.get(storeKey);
  if (!inflight) {
    inflight = (async () => {
      await Statsig.updateUser(buildStatsigUser(store, currentUser));
      const enabled = Statsig.checkGate(flag);
      gateState.cache.set(storeKey, enabled);
      return enabled;
    })().finally(() => {
      gateState.inflight.delete(storeKey);
    });
    gateState.inflight.set(storeKey, inflight);
  }

  return inflight;
};

// Re-checks when the selected store changes; skips re-check when remounting for the same store.
export const useStoreStatsigGate = (
  flag: STATSIG_FLAGS,
  store: EmployeeStore | undefined,
  currentUser: User | null | undefined,
): boolean => {
  const storeKey = store && currentUser ? getStoreStatsigKey(store) : null;
  const gateState = getStoreGateState(flag);
  const [enabled, setEnabled] = useState(() => (storeKey ? gateState.cache.get(storeKey) ?? false : false));
  const activeStoreKeyRef = useRef(storeKey);

  useEffect(() => {
    activeStoreKeyRef.current = storeKey;

    if (!store || !currentUser || !storeKey) {
      setEnabled(false);
      return;
    }

    const storeSwitched =
      gateState.lastSelectedStoreKey !== null && gateState.lastSelectedStoreKey !== storeKey;
    const cached = gateState.cache.get(storeKey);

    if (cached !== undefined && !storeSwitched) {
      setEnabled(cached);
      return;
    }

    gateState.lastSelectedStoreKey = storeKey;
    let cancelled = false;

    checkStoreGateForStore(flag, store, currentUser).then((value) => {
      if (!cancelled && activeStoreKeyRef.current === storeKey) {
        setEnabled(value);
      }
    });

    return () => {
      cancelled = true;
    };
    // storeKey only — avoid re-checking when parent re-renders with a new store object reference.
  }, [flag, storeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return enabled;
};

export const useSurprisePackHandlingEnabled = (
  store: EmployeeStore | undefined,
  currentUser: User | null | undefined,
): boolean => useStoreStatsigGate(STATSIG_FLAGS.SURPRISE_PACK_HANDLING, store, currentUser);

export const useChatbotEnabled = (
  store: EmployeeStore | undefined,
  currentUser: User | null | undefined,
): boolean => useStoreStatsigGate(STATSIG_FLAGS.CHATBOT, store, currentUser);

// prints the name of the component that mounts/unmounts every time it does so
// custom hook to help debug navigation issues
// usage: useMountLogs('MyComponent');
// enable verbose logging in the console to see these debug logs
export const useMountLogs = (name: string) => {
  useEffect(() => {
    console.debug(name, 'mounted');
    return () => {
      console.debug(name, 'unmounted');
    };
  }, [name]);
};

// custom hook to debug dependency changes in useEffect. drop-in replacement for useEffect.
export const useEffectDebugger = (effectHook: any, dependencies: any, dependencyNames: any = []) => {
  const previousDeps = usePrevious(dependencies, []);

  const changed = getChangedDeps(previousDeps, dependencies, dependencyNames);

  if (Object.keys(changed).length) {
    console.log('useEffectDebugger ', changed);
  }

  useEffect(effectHook, dependencies); // eslint-disable-line react-hooks/exhaustive-deps
};

// custom hook to debug dependency changes in useCallback. drop-in replacement for useCallback.
export const useCallbackDebugger = (callbackHook: any, dependencies: any, dependencyNames: any = []) => {
  const previousDeps = usePrevious(dependencies, []);

  const changed = getChangedDeps(previousDeps, dependencies, dependencyNames);

  if (Object.keys(changed).length) {
    console.log('useCallbackDebugger ', changed);
  }

  return useCallback(callbackHook, dependencies); // eslint-disable-line react-hooks/exhaustive-deps
};

const usePrevious = (value: any, initialValue: any) => {
  const ref = useRef(initialValue);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

const getChangedDeps = (previousDeps: any, dependencies: any, dependencyNames: any = []) => {
  const changedDeps = dependencies.reduce((accum: any, dependency: any, index: any) => {
    if (dependency !== previousDeps[index]) {
      const keyName = dependencyNames[index] || index;
      return {
        ...accum,
        [keyName]: {
          before: previousDeps[index],
          after: dependency,
        },
      };
    }

    return accum;
  }, {});

  return changedDeps;
};
